import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trophy, Mail, Lock, User, ArrowLeft, KeyRound, RefreshCw, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function TurnstileWidget({ onVerify, onError, resetKey }) {
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const [siteKey, setSiteKey] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/auth/turnstile-key`)
      .then(res => setSiteKey(res.data.site_key))
      .catch(() => console.error('Error cargando Turnstile key'));
  }, []);

  useEffect(() => {
    if (!siteKey) return;

    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => renderWidget();
      document.head.appendChild(script);
    } else {
      renderWidget();
    }

    function renderWidget() {
      if (containerRef.current && window.turnstile) {
        if (widgetId.current !== null) {
          try { window.turnstile.remove(widgetId.current); } catch {}
        }
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerify(token),
          'error-callback': () => onError && onError(),
          theme: 'dark',
          size: 'normal'
        });
      }
    }

    return () => {
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch {}
      }
    };
  }, [siteKey, onVerify, onError, resetKey]);

  if (!siteKey) {
    return (
      <div className="flex items-center justify-center p-4 bg-slate-800 rounded-lg">
        <RefreshCw className="w-4 h-4 animate-spin text-slate-500 mr-2" />
        <span className="text-sm text-slate-400">Cargando verificacion...</span>
      </div>
    );
  }

  return <div ref={containerRef} className="flex justify-center"></div>;
}

export default function LigasLoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [codigo, setCodigo] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const resetTurnstile = () => {
    setTurnstileToken(null);
    setTurnstileResetKey(prev => prev + 1);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      if (response.data?.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      toast.success('Bienvenido');
      window.location.replace('/ligas/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al iniciar sesion';
      if (detail.includes('no verificado')) {
        toast.info('Revisa tu email para el codigo de verificacion');
        setMode('verify');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    if (!turnstileToken) {
      toast.error('Por favor completa la verificacion de seguridad');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/register`, {
        email, password, nombre,
        turnstile_token: turnstileToken
      });
      toast.success('Registro exitoso. Revisa tu email para el codigo');
      setMode('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar');
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/verificar-codigo`, { email, codigo }, { withCredentials: true });
      toast.success('Email verificado. Bienvenido!');
      window.location.replace('/ligas/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codigo invalido');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await axios.post(`${API}/api/auth/reenviar-codigo`, { email });
      toast.success('Codigo reenviado');
    } catch (error) {
      toast.error('Error al reenviar codigo');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Ingresa tu correo electronico'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/solicitar-recuperacion`, { email });
      toast.success('Si el email existe, recibiras instrucciones');
      setMode('forgot-sent');
    } catch (error) {
      toast.error('Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4 text-slate-400 hover:text-white"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al inicio
        </Button>

        <Card className="p-8 shadow-2xl bg-slate-800 border-slate-700">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-slate-900" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="ligas-login-title">
              Ligas Deportivas
            </h1>
            <p className="text-slate-400 text-sm">
              {mode === 'login' && 'Ingresa con tu cuenta'}
              {mode === 'register' && 'Crea tu cuenta gratis'}
              {mode === 'verify' && 'Verifica tu email'}
              {mode === 'forgot' && 'Recupera tu contrasena'}
              {mode === 'forgot-sent' && 'Revisa tu correo'}
            </p>
          </div>

          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="ligas-login-form">
              <div>
                <Label className="text-slate-300">Correo electronico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    required className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    placeholder="tu@email.com" data-testid="ligas-login-email"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Contrasena</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    required className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    placeholder="********" data-testid="ligas-login-password"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200 font-semibold" disabled={loading} data-testid="ligas-login-submit">
                {loading ? 'Ingresando...' : 'Ingresar'}
              </Button>
              <button type="button" onClick={() => setMode('forgot')} className="w-full text-sm text-slate-400 hover:text-white" data-testid="ligas-forgot-link">
                Olvidaste tu contrasena?
              </button>
              <p className="text-center text-sm text-slate-400 mt-4">
                No tienes cuenta?{' '}
                <button type="button" onClick={() => setMode('register')} className="text-white hover:underline font-medium" data-testid="ligas-register-link">
                  Registrate gratis
                </button>
              </p>
            </form>
          )}

          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="ligas-register-form">
              <div>
                <Label className="text-slate-300">Nombre completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} required
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" placeholder="Tu nombre"
                    data-testid="ligas-register-name" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Correo electronico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" placeholder="tu@email.com"
                    data-testid="ligas-register-email" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Contrasena</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" placeholder="Minimo 6 caracteres"
                    data-testid="ligas-register-password" />
                </div>
              </div>
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-400">Verificacion de seguridad</span>
                </div>
                <TurnstileWidget onVerify={(token) => setTurnstileToken(token)} onError={() => toast.error('Error en la verificacion')} resetKey={turnstileResetKey} />
              </div>
              <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200 font-semibold" disabled={loading || !turnstileToken} data-testid="ligas-register-submit">
                {loading ? 'Registrando...' : 'Crear cuenta'}
              </Button>
              <p className="text-center text-sm text-slate-400 mt-4">
                Ya tienes cuenta?{' '}
                <button type="button" onClick={() => { setMode('login'); resetTurnstile(); }} className="text-white hover:underline font-medium" data-testid="ligas-login-link">
                  Inicia sesion
                </button>
              </p>
            </form>
          )}

          {mode === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4" data-testid="ligas-verify-form">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                  <KeyRound className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm text-slate-400">
                  Enviamos un codigo de 6 digitos a<br />
                  <span className="font-medium text-white">{email}</span>
                </p>
              </div>
              <div>
                <Label className="text-slate-300">Codigo de verificacion</Label>
                <Input type="text" value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required maxLength={6} className="text-center text-2xl tracking-widest font-mono bg-slate-700 border-slate-600 text-white"
                  placeholder="000000" data-testid="ligas-verify-code" />
              </div>
              <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200 font-semibold" disabled={loading || codigo.length !== 6} data-testid="ligas-verify-submit">
                {loading ? 'Verificando...' : 'Verificar'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={handleResend} className="text-sm text-slate-400 hover:text-white" data-testid="ligas-resend-code">
                  No recibiste el codigo? Reenviar
                </button>
              </div>
              <button type="button" onClick={() => setMode('login')} className="w-full text-sm text-slate-400 hover:text-white" data-testid="ligas-back-to-login">
                Volver al inicio de sesion
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="ligas-forgot-form">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm text-slate-400">Ingresa tu correo y te enviaremos un enlace para restablecer tu contrasena</p>
              </div>
              <div>
                <Label className="text-slate-300">Correo electronico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" placeholder="tu@email.com"
                    data-testid="ligas-forgot-email" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200 font-semibold" disabled={loading} data-testid="ligas-forgot-submit">
                {loading ? 'Enviando...' : 'Enviar enlace'}
              </Button>
              <button type="button" onClick={() => setMode('login')} className="w-full text-sm text-slate-400 hover:text-white" data-testid="ligas-back-login-forgot">
                Volver al inicio de sesion
              </button>
            </form>
          )}

          {mode === 'forgot-sent' && (
            <div className="text-center space-y-4" data-testid="ligas-forgot-sent">
              <div className="w-16 h-16 bg-emerald-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Correo enviado!</h3>
              <p className="text-sm text-slate-400">
                Si existe una cuenta con el correo <span className="font-medium text-white">{email}</span>,
                recibiras un enlace para restablecer tu contrasena.
              </p>
              <p className="text-xs text-slate-500">Revisa tambien tu carpeta de spam si no lo encuentras.</p>
              <Button onClick={() => setMode('login')} variant="outline" className="w-full mt-4 border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="ligas-back-login-sent">
                Volver al inicio de sesion
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
